import {
  getReverseChargeContextFromJson,
  shouldShowReverseChargeMessage,
} from "../reverseCharge";

/**
 * Effective document total for invoice/bill review display.
 * Bill: sub_total + tax_total + TCS when applied, OR (sub_total + tax_total - TDS) when TDS on line items.
 * Reverse charge (overseas / RCM vendor with taxed lines): payable total excludes tax_total.
 */
export function getEffectiveTotalFromRawJson(
  rawJson: Record<string, unknown> | null | undefined,
  moduleCode?: string,
  gstTreatment?: string,
  gstCategory?: string,
): number {
  if (!rawJson || typeof rawJson !== "object") return 0;
  const subTotal = Number(rawJson.sub_total) || 0;
  const taxTotal = Number(rawJson.tax_total) || 0;
  const discount = Number(rawJson.discount) || 0;
  const isBill = (moduleCode || "").toLowerCase() === "bill";
  const items = (rawJson.line_items as Record<string, unknown>[]) || [];

  const rcContext = getReverseChargeContextFromJson(
    rawJson,
    gstTreatment,
    gstCategory,
  );
  const isReverseCharge = rcContext.showMessage;

  const hasTds =
    isBill &&
    Array.isArray(items) &&
    items.some(
      (item) =>
        item.tds_tax_percentage != null &&
        Number(item.tds_tax_percentage) > 0,
    );

  let tdsTotal = 0;
  if (hasTds) {
    tdsTotal = items.reduce((sum: number, item) => {
      if (
        item.tds_tax_percentage != null &&
        Number(item.tds_tax_percentage) > 0
      ) {
        const lineAmount =
          (Number(item.rate) || 0) * (Number(item.quantity) || 0);
        return (
          sum + (lineAmount * (Number(item.tds_tax_percentage) || 0)) / 100
        );
      }
      return sum;
    }, 0);
  }

  const tcsTaxId = rawJson.tcs_tax_id;
  const tcsPct = Number(rawJson.tcs_tax_percentage) || 0;
  const isTcsApplied = !!(tcsTaxId && tcsTaxId !== "" && tcsPct > 0);
  const tcsBase = isReverseCharge ? subTotal : subTotal + taxTotal;
  const tcsTotal = isTcsApplied ? tcsBase * (tcsPct / 100) : 0;

  if (isReverseCharge) {
    return subTotal + discount - tdsTotal + tcsTotal;
  }

  if (hasTds) {
    return subTotal + taxTotal - tdsTotal;
  }

  return subTotal + taxTotal + tcsTotal;
}

/** Whether reverse charge display rules apply for a document JSON snapshot. */
export function isReverseChargeDocumentJson(
  rawJson: Record<string, unknown> | null | undefined,
  gstTreatment?: string,
  gstCategory?: string,
): boolean {
  return shouldShowReverseChargeMessage(
    gstTreatment ?? String(rawJson?.gst_treatment ?? ""),
    gstCategory ?? String(rawJson?.gst_category ?? ""),
    (rawJson?.line_items as Array<{ tax_id?: string | null }>) ?? [],
  );
}
